// src/components/operationCenter/widgets/thesis/ThesisWidget.tsx
// Main thesis widget for the Operation Center dashboard

import React, { useMemo } from "react";
import type { WidgetProps } from "@/types/dashboard";
import type { ThesisWidgetConfig } from "@/types/thesis";
import {
  useThesisById,
  useThesisByDashboard,
  useThesisConditions,
  useThesisHealth,
} from "@/stores/thesisStore";
import { useActiveDashboard } from "@/stores/dashboardStore";
import { ThesisStatusBadge } from "./ThesisStatusBadge";
import { ThesisHealthGauge } from "./ThesisHealthGauge";
import { ThesisConditionList } from "./ThesisConditionList";
import { FileQuestion, ChevronRight } from "lucide-react";

interface ThesisWidgetProps extends WidgetProps<ThesisWidgetConfig> {
  onOpenThesis?: (thesisId: string) => void;
}

const ThesisWidget: React.FC<ThesisWidgetProps> = ({
  widget,
  onOpenThesis,
}) => {
  const config = widget.config as ThesisWidgetConfig;
  const activeDashboard = useActiveDashboard();

  // Resolve thesis ID using the 1:1 dashboard relationship:
  // 1. Explicit thesisId in widget config (highest priority)
  // 2. Dashboard's thesisId
  // 3. Look up thesis by dashboard ID (1:1 relationship)
  const effectiveThesisId = config.thesisId || activeDashboard?.thesisId;

  // Look up thesis by ID first, then by dashboard (1:1 relationship)
  const thesisByDirectId = useThesisById(effectiveThesisId);
  const thesisByDashboard = useThesisByDashboard(activeDashboard?.id);

  // Use direct thesis ID if available, otherwise fall back to dashboard lookup
  const thesis = thesisByDirectId ?? thesisByDashboard;
  const conditions = useThesisConditions(thesis?.id);
  const health = useThesisHealth(thesis?.id);

  // Calculate conditions summary
  const conditionsSummary = useMemo(() => {
    if (!conditions || conditions.length === 0) {
      return { met: 0, total: 0 };
    }
    return {
      met: conditions.filter((c) => c.isMet).length,
      total: conditions.length,
    };
  }, [conditions]);

  // Handle click to open thesis detail
  const handleOpenThesis = () => {
    if (thesis && onOpenThesis) {
      onOpenThesis(thesis.id);
    }
  };

  // No thesis for this dashboard
  if (!thesis) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
          <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
            {widget.title ?? "Thesis"}
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <FileQuestion className="w-8 h-8 text-slate-600 mb-2" />
          <p className="text-[11px] text-slate-500">No thesis defined</p>
          <p className="text-[10px] text-slate-600 mt-1">
            Create a thesis to track your investment hypothesis
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
            {widget.title ?? thesis.title}
          </span>
          <ThesisStatusBadge status={thesis.status} size="sm" />
        </div>
        {onOpenThesis && (
          <button
            onClick={handleOpenThesis}
            className="p-1 rounded hover:bg-slate-700/50 transition-colors"
            title="Open thesis detail"
          >
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-3 min-h-0 overflow-y-auto">
        {/* Health gauge section */}
        <div className="flex items-center justify-center mb-3">
          <ThesisHealthGauge
            score={health?.score ?? 0}
            trend={health?.trend ?? "stable"}
            conditionsMet={conditionsSummary.met}
            conditionsTotal={conditionsSummary.total}
            compact={false}
          />
        </div>

        {/* Thesis description */}
        {thesis.description && (
          <p className="text-[10px] text-slate-400 mb-3 line-clamp-2">
            {thesis.description}
          </p>
        )}

        {/* Conditions list */}
        <div className="flex-1 min-h-0">
          <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-1.5 px-1">
            Conditions
          </div>
          <ThesisConditionList
            conditions={conditions}
            maxVisible={4}
            compact={true}
          />
        </div>

        {/* AI Summary (if available) */}
        {thesis.aiSummary && (
          <div className="mt-3 pt-2 border-t border-slate-700/30">
            <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-1">
              AI Analysis
            </div>
            <p className="text-[10px] text-slate-400 line-clamp-2">
              {thesis.aiSummary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThesisWidget;
